import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { Check, ChevronRight, Loader2, Send, X } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { FileCard } from "@/shared/components/ui/file-card";
import { usePerson } from "@/shared/hooks/useAppContext";
import { uploadDocument, DocumentLimitError } from "@/shared/utils/uploadDocument";
import { EventType, setPersonProperties, trackEvent } from "@/shared/utils/core";
import { getFileTypes, toErrorType } from "@/shared/utils/funnelAnalytics";

import uploadGraphic from "@/shared/assets/illustrations/upload-graphic.svg";
import takePictureGraphic from "@/shared/assets/illustrations/take-picture.svg";
import inviteFamilyGraphic from "@/shared/assets/illustrations/invite-family.svg";

import { TriageModalStepLayout } from "./TriageModalStepLayout";
import { IconButton } from "@/shared/components/ui/icon-button";

let nextFileId = 0;

interface StagedFile {
  id: number;
  documentId?: number;
  file: File;
  status: "staged" | "complete" | "error";
  errorMessage: string | null;
}

export interface CompletedUpload {
  documentId?: number;
  fileName: string;
  fileSize: number;
}

interface TriageModalDeathCertificateStepProps {
  personFirstName: string;
  onBack: () => void;
  onContinue: (hasUploads: boolean) => void;
  onPhoneUpload: () => void;
  onRequestUpload: () => void;
  completedUploads: CompletedUpload[];
  onUploadCompleted: (upload: CompletedUpload) => void;
  onUploadRemoved: (fileName: string, documentId?: number) => void;
  requestedFrom?: string | null;
}

export const TriageModalDeathCertificateStep: FC<TriageModalDeathCertificateStepProps> = ({
  personFirstName,
  onBack,
  onContinue,
  onPhoneUpload,
  onRequestUpload,
  completedUploads,
  onUploadCompleted,
  onUploadRemoved,
  requestedFrom = null,
}) => {
  const person = usePerson();
  const estateId = person?.estate_id;

  const [showRequestBanner, setShowRequestBanner] = useState(!!requestedFrom);
  const prevRequestedFromRef = useRef(requestedFrom);
  useEffect(() => {
    if (requestedFrom && !prevRequestedFromRef.current) {
      setShowRequestBanner(true);
    }
    prevRequestedFromRef.current = requestedFrom;
  }, [requestedFrom]);
  const [stagedFiles, setStagedFiles] = useState<StagedFile[]>(() =>
    completedUploads.map((f) => ({
      id: nextFileId++,
      documentId: f.documentId,
      file: new File([], f.fileName, { type: "application/pdf" }),
      status: "complete" as const,
      errorMessage: null,
    })),
  );
  const prevCompletedCountRef = useRef(completedUploads.length);
  useEffect(() => {
    if (completedUploads.length > prevCompletedCountRef.current) {
      const existingIds = new Set(stagedFiles.filter((f) => f.documentId).map((f) => f.documentId));
      const newUploads = completedUploads
        .filter((f) => f.documentId && !existingIds.has(f.documentId))
        .map((f) => ({
          id: nextFileId++,
          documentId: f.documentId,
          file: new File([], f.fileName, { type: "application/pdf" }),
          status: "complete" as const,
          errorMessage: null,
        }));
      if (newUploads.length > 0) {
        setStagedFiles((prev) => [...prev, ...newUploads]);
      }
    }
    prevCompletedCountRef.current = completedUploads.length;
  }, [completedUploads, stagedFiles]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      trackEvent(EventType.DEATH_CERTIFICATE_METHOD_SELECTED, {
        estate_id: estateId != null ? String(estateId) : undefined,
        method: "computer",
      });
      const newFiles: StagedFile[] = Array.from(files).map((file) => ({
        id: nextFileId++,
        file,
        status: "staged" as const,
        errorMessage: null,
      }));
      setStagedFiles((prev) => [...prev, ...newFiles]);
      setUploadError(undefined);
    },
    [estateId],
  );

  const handleContinue = async () => {
    if (!estateId) return;
    const pendingFiles = stagedFiles.filter((f) => f.status === "staged");

    if (stagedFiles.length === 0 && !requestedFrom) return;

    if (pendingFiles.length === 0) {
      onContinue(stagedFiles.length > 0);
      return;
    }

    setIsUploading(true);
    setUploadError(undefined);

    try {
      for (const staged of pendingFiles) {
        const docId = await uploadDocument(staged.file, "death_certificate", estateId);
        setStagedFiles((prev) =>
          prev.map((f) =>
            f.id === staged.id ? { ...f, documentId: docId, status: "complete" } : f,
          ),
        );
        onUploadCompleted({
          documentId: docId,
          fileName: staged.file.name,
          fileSize: staged.file.size,
        });
      }
      trackEvent(EventType.DEATH_CERTIFICATE_UPLOADED, {
        estate_id: String(estateId),
        upload_method: "computer",
        file_count: pendingFiles.length,
        file_type: getFileTypes(pendingFiles.map((f) => f.file.name)),
      });
      setPersonProperties({}, { death_certificate_upload_method: "computer" });
      onContinue(true);
    } catch (err) {
      trackEvent(EventType.DEATH_CERTIFICATE_UPLOAD_FAILED, {
        estate_id: String(estateId),
        upload_method: "computer",
        error_type: toErrorType(err),
      });
      const message =
        err instanceof DocumentLimitError ? err.message : "The upload didn't finish. Try again.";
      setUploadError(message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDropZoneClick = () => fileInputRef.current?.click();
  const handleAddAnother = () => additionalInputRef.current?.click();
  const handleRemoveFile = (fileId: number) => {
    const removed = stagedFiles.find((f) => f.id === fileId);
    if (removed?.status === "complete") {
      onUploadRemoved(removed.file.name, removed.documentId);
    }
    setStagedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  };

  const hasFiles = stagedFiles.length > 0;
  const fileCount = stagedFiles.length;

  return (
    <TriageModalStepLayout
      title="Add the death certificate"
      description={`One upload confirms ${personFirstName || "the person who passed"}'s details and sets up the right steps for the estate. It's saved for anywhere else that asks.`}
      showVaultNotice
      onBack={onBack}
      actionButton={
        <Button
          variant="brand"
          size="md"
          onClick={handleContinue}
          disabled={isUploading || (stagedFiles.length === 0 && !requestedFrom)}
        >
          {isUploading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Uploading...
            </>
          ) : (
            "Continue"
          )}
        </Button>
      }
    >
      {showRequestBanner && requestedFrom && (
        <div className="flex items-center gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
          <Send className="h-4 w-4 shrink-0 text-brand" />
          <p className="flex-1 text-sm font-semibold">
            Requested from {requestedFrom}
            <span className="ml-2 font-normal text-secondary">Sent now</span>
          </p>
          <IconButton onClick={() => setShowRequestBanner(false)} label="Dismiss" tooltip={false}>
            <X />
          </IconButton>
        </div>
      )}

      {requestedFrom && !hasFiles ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm font-semibold text-primary">Have it sooner?</p>

          <button
            type="button"
            onClick={handleDropZoneClick}
            className="flex items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
          >
            <img
              src={uploadGraphic}
              alt="Browse files"
              className="h-16 w-16 shrink-0 object-contain"
            />
            <p className="flex-1 min-w-0 text-left text-sm font-medium text-primary">
              Browse files
            </p>
            <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />

          <button
            type="button"
            onClick={onPhoneUpload}
            className="hidden md:flex items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
          >
            <img
              src={takePictureGraphic}
              alt="Take a picture with your phone"
              className="h-16 w-16 shrink-0 object-contain"
            />
            <p className="flex-1 min-w-0 text-left text-sm font-medium text-primary">
              Take a picture
            </p>
            <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
          </button>
        </div>
      ) : (
        <>
          <hr className="border-primary" />

          <div className="flex flex-col gap-4">
            {hasFiles ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-primary">
                    {fileCount} {fileCount === 1 ? "document" : "documents"} added
                  </p>
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-brand">
                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                  </div>
                </div>
                {stagedFiles.map((staged) => (
                  <FileCard
                    key={staged.id}
                    fileName={staged.file.name}
                    fileSize={staged.file.size}
                    onRemove={() => handleRemoveFile(staged.id)}
                    status="complete"
                    progress={100}
                    errorMessage={staged.errorMessage ?? undefined}
                  />
                ))}
                <button
                  type="button"
                  onClick={handleAddAnother}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-primary bg-white px-4 py-3 text-sm font-medium text-primary transition-colors hover:bg-subtle"
                >
                  + Add another
                </button>
                <input
                  ref={additionalInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
              </div>
            ) : (
              <button
                type="button"
                className="flex h-[220px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-primary bg-white px-6 py-8 transition-colors hover:border-focus"
                onClick={handleDropZoneClick}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                aria-label="Choose a file or drag and drop"
              >
                <img
                  src={uploadGraphic}
                  alt="Upload documents"
                  className="h-[90px] w-[90px] object-contain"
                />
                <p className="text-base font-medium tracking-[-0.32px] text-primary">
                  Choose a file
                </p>
                <p className="text-sm text-secondary">
                  Click here to pick the certificate from your files, or drag it in
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
              </button>
            )}

            {!hasFiles && (
              <>
                <div className="flex items-center gap-6">
                  <div className="h-px flex-1 bg-stone-200" aria-hidden="true" />
                  <p className="shrink-0 text-2xs text-tertiary">OR</p>
                  <div className="h-px flex-1 bg-stone-200" aria-hidden="true" />
                </div>

                <div className="flex flex-col gap-4 sm:flex-row">
                  <button
                    type="button"
                    onClick={onPhoneUpload}
                    className="hidden md:flex flex-1 items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
                  >
                    <img
                      src={takePictureGraphic}
                      alt="Take a picture with your phone"
                      className="h-16 w-16 shrink-0 object-contain"
                    />
                    <p className="flex-1 min-w-0 text-left text-sm font-medium text-primary">
                      Take a picture with your phone
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
                  </button>

                  <button
                    type="button"
                    className="flex flex-1 items-center gap-4 rounded-lg border border-primary bg-white p-4 transition-colors hover:bg-subtle"
                    onClick={onRequestUpload}
                  >
                    <img
                      src={inviteFamilyGraphic}
                      alt="Ask someone else to upload it"
                      className="h-16 w-16 shrink-0 object-contain"
                    />
                    <p className="flex-1 min-w-0 text-left text-sm font-medium text-primary">
                      Ask someone else to upload it
                    </p>
                    <ChevronRight className="h-5 w-5 shrink-0 text-tertiary" />
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <FieldError message={uploadError} />
    </TriageModalStepLayout>
  );
};
